/* Organizer Website — mandatory hero (image / video / carousel).
   Video reuses the platform media pipeline: uploaded file streams from the
   media map with the poster image shown until playback can start; external
   URLs play directly. Carousel crossfades. Always renders something premium
   even with zero organizer input (name + description on deep ink). */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { localized } from "@/lib/intl";
import { mediaUrlFromMap } from "@/lib/api";

const HeroMedia = ({ hero, media, organizerName }) => {
  const posterUrl = mediaUrlFromMap(media, hero.media_id);
  const videoUrl = hero.video_url || mediaUrlFromMap(media, hero.video_media_id);
  const carouselUrls = (hero.carousel_media_ids || []).map((id) => mediaUrlFromMap(media, id)).filter(Boolean);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (hero.type !== "carousel" || carouselUrls.length < 2) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % carouselUrls.length), 5000);
    return () => clearInterval(id);
  }, [hero.type, carouselUrls.length]);

  if (hero.type === "video" && videoUrl) {
    return (
      <video
        src={videoUrl}
        poster={posterUrl || undefined}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        data-testid="website-hero-video"
        className="absolute inset-0 w-full h-full object-cover opacity-45"
        aria-hidden="true"
      />
    );
  }
  if (hero.type === "carousel" && carouselUrls.length) {
    return (
      <div className="absolute inset-0" data-testid="website-hero-carousel" aria-hidden="true">
        {carouselUrls.map((url, i) => (
          <img
            key={url}
            src={url}
            alt=""
            loading={i === 0 ? "eager" : "lazy"}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              i === slide ? "opacity-45" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {carouselUrls.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`${i + 1}`}
              data-testid={`website-hero-dot-${i}`}
              onClick={() => setSlide(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === slide ? "w-6 bg-white" : "w-1.5 bg-white/40"}`}
            />
          ))}
        </div>
      </div>
    );
  }
  if (posterUrl) {
    return (
      <img
        src={posterUrl}
        alt={organizerName}
        loading="eager"
        data-testid="website-hero-image"
        className="absolute inset-0 w-full h-full object-cover opacity-45"
      />
    );
  }
  return null;
};

export const WebsiteHero = ({ organizer, website, media }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const hero = website?.hero || {};
  const headline = localized(hero.headline, locale) || organizer.name;
  const subheadline = localized(hero.subheadline, locale) || localized(organizer.description, locale);

  const scrollTo = (anchor) => document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section className="relative bg-[#0A0A0A] overflow-hidden eq-grain" data-testid="website-hero">
      <HeroMedia hero={hero} media={media} organizerName={organizer.name} />
      <div className="relative max-w-6xl mx-auto px-6 lg:px-12 py-28 lg:py-40 flex flex-col gap-7">
        <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/55">{organizer.name}</span>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white max-w-3xl leading-[1.05] brand-heading"
          data-testid="website-hero-headline"
        >
          {headline}
        </h1>
        {subheadline && (
          <p className="text-base lg:text-lg text-white/70 leading-relaxed max-w-2xl">{subheadline}</p>
        )}
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button
            type="button"
            data-testid="website-hero-cta"
            onClick={() => scrollTo("auctions")}
            className="inline-flex items-center h-12 px-8 rounded-full bg-white text-[#0A0A0A] text-sm font-semibold hover:bg-white/90 transition-colors duration-300"
          >
            {t("web.viewAuctions")}
          </button>
          <button
            type="button"
            data-testid="website-hero-htb"
            onClick={() => scrollTo("how-to-bid")}
            className="inline-flex items-center h-12 px-7 rounded-full border border-white/25 text-white text-sm font-medium hover:bg-white/10 transition-colors duration-300"
          >
            {t("web.howToBid")}
          </button>
        </div>
      </div>
    </section>
  );
};
